// Single source of truth for the words in ChapterHandbook (portal DOM) and
// ChapterHandbookPDF (print). Rendered by ../documents/DocContent.jsx and
// ../pdf/PdfContent.jsx — the block shape is documented in DocContent.jsx.
// pdf* fields and the dom:/pdf: switches mark where the print copy
// deliberately diverges from the portal copy.

export const docTitle = "GNF chapter handbook";

export const cover = {
  eyebrow: "Operations · 02",
  number: "02",
  title: "The chapter handbook",
  summary: "Everything you need to launch a Good Neighbor Fund neighborhood. Mission, roles, timeline, operations, and the answers to common questions.",
  accent: "grape",
};

export const blocks = [
  {
    type: "lede",
    text: "Welcome. You're here because you're interested in launching a Good Neighbor Fund chapter, or as we call it, a Neighborhood. We're glad you found us.",
  },
  { type: "heading", level: 1, text: "Introduction" },
  {
    type: "p",
    runs: [
      "GNF started in Buffalo, NY, with a simple idea. Bold, early-stage entrepreneurs, especially those from underrepresented backgrounds, deserve belief, not the ",
      { i: "bureaucracy" },
      " they typically encounter with early-stage business organizations. So we give out $1,000 micro-grants (belief capital) to help founders take that first step.",
    ],
  },
  {
    type: "p",
    runs: [
      "Since 2023, we've backed dozens of founders, built a grassroots LP (Limited Partner) community, and put together a replicable model that blends funding, mentorship, and local pride. Now we're opening up the playbook. Whether you're a founder, community builder, or startup cheerleader, this handbook is your roadmap to bringing GNF to your city.",
    ],
    pdfRuns: [
      "Since 2023, we've backed dozens of founders, built a grassroots LP community, and put together a replicable model that blends funding, mentorship, and local pride. Now we're opening up the playbook.",
    ],
  },
  { type: "heading", level: 1, text: "Our mission" },
  { type: "p", runs: ["We level the playing field for entrepreneurship by:"] },
  {
    type: "list",
    items: [
      { runs: ["Awarding no-strings-attached $1,000 micro-grants"] },
      { runs: ["Supporting underrepresented and under-resourced founders"] },
      { runs: ["Building local chapters powered by community-driven LPs"] },
    ],
  },
  {
    type: "p",
    runs: [
      "GNF is proudly national, proudly weird, and deeply committed to doing good in the neighborhoods we serve.",
    ],
  },
  { type: "heading", level: 1, text: "What's a GNF neighborhood?" },
  { type: "p", runs: ["A GNF Neighborhood is a local group of Limited Partners (LPs) who:"] },
  {
    type: "list",
    items: [
      { runs: ["Pool their money to fund quarterly $1,000 micro-grants"] },
      { runs: ["Meet to review pitches and vote on winners"] },
      { runs: ["Support local founders through connections, mentorship, and visibility"] },
    ],
  },
  {
    type: "p",
    runs: [
      "Each chapter is led by a Chapter Director, supported by GNF HQ, and connected to a national community of neighborhoods.",
    ],
  },
  { type: "heading", level: 1, text: "Starting a chapter", pdfBreak: true },
  { type: "heading", level: 2, text: "Step 1. Say hi" },
  {
    type: "p",
    runs: [
      "Fill out our short ",
      {
        a: {
          href: "https://goodneighbor.fund/start-a-chapter",
          text: "new chapter interest form",
          external: true,
        },
      },
      ".",
    ],
    pdfRuns: [
      "Fill out our short new-chapter interest form at goodneighbor.fund/start-a-chapter.",
    ],
  },
  { type: "heading", level: 2, text: "Step 2. Recruit your LPs" },
  {
    type: "p",
    runs: ["You'll need roughly 6–10 founding LPs (that number is up to you). Typically, LPs are:"],
    pdfRuns: ["You'll need roughly 6 to 10 founding LPs. Typically, LPs are:"],
  },
  {
    type: "list",
    items: [
      {
        runs: ["Founders, executives, creatives, or active community leaders"],
        pdfRuns: ["Founders, executives, creatives, or community leaders"],
      },
      { runs: ["Actively involved in the local entrepreneurial ecosystem"] },
      { runs: ["Excited to fund and support early-stage businesses"] },
    ],
  },
  {
    type: "p",
    runs: [
      "Each LP contributes $500/year, ideally upfront. Some chapters add a small margin to cover admin fees (e.g., $525/year).",
    ],
  },
  {
    type: "callout",
    label: "Your Chapter, Your Numbers",
    runs: [
      "The LP membership amount and the micro-grant amount issued is determined by your specific chapter's needs. We don't dictate that $1,000 must be the standard. We're always open to a conversation to help you determine the right fit for your area.",
    ],
  },
  { type: "heading", level: 2, text: "Step 3. Host your first meeting" },
  { type: "p", runs: ["Plan your inaugural LP meetup to:"] },
  {
    type: "list",
    items: [
      { runs: ["Get to know each other"] },
      { runs: ["Review early pitch submissions"] },
      { runs: ["Select your first grantee (or plan when you'll open your first cycle)"] },
    ],
  },
  {
    type: "p",
    runs: [
      "Make it fun. We recommend restaurants, cafes, rooftops, or breweries. Somewhere casual, inspiring, and local.",
    ],
    pdfRuns: [
      "Make it fun. Restaurants, cafes, rooftops, or breweries. Somewhere casual, inspiring, and local.",
    ],
  },
  {
    type: "callout",
    label: "Best Practice",
    runs: [
      "Use an online poll to align on dates, pick a restaurant, and be mindful of your LPs' dietary restrictions or alcohol preferences.",
    ],
  },
  { type: "heading", level: 1, text: "Y2K energy", pdfBreak: true },
  {
    type: "p",
    runs: [
      "The aesthetic is on purpose. We're inspired by a time when people just ",
      { i: "started" },
      ". The early internet era was raw, open, and full of possibility. That's how we want our founders to feel.",
    ],
  },
  {
    type: "p",
    runs: ["More importantly: we back ideas before they're buttoned up. We fund courage, not polish."],
  },
  { type: "heading", level: 1, text: "How chapters operate" },
  { type: "heading", level: 2, text: "Grant timeline" },
  {
    type: "list",
    items: [
      {
        runs: ["Most chapters operate quarterly, but monthly or biannual is fine too."],
        pdfRuns: ["Most chapters operate quarterly; monthly or biannual is fine too."],
      },
      { runs: ["LPs vote on grant winners based on pitch applications and 60-second videos."] },
      { runs: ["The Chapter Director facilitates meetings and follows up with grantees."] },
    ],
  },
  { type: "heading", level: 2, text: "Grant criteria", pdf: false },
  {
    type: "heading",
    level: 3,
    text: "What we look for",
    pdfText: "Grant criteria: what we look for",
    pdfLevel: 2,
  },
  {
    type: "list",
    items: [
      { runs: ["Passionate, ideation or early-stage founders"] },
      { runs: ["Clear problem/solution articulation"] },
      { runs: ["Specific, high-impact use of the $1,000 grant"] },
    ],
  },
  { type: "heading", level: 3, text: "What we avoid", pdfLevel: 2 },
  {
    type: "list",
    items: [
      {
        runs: ["Personal expenses (e.g., rent, bills)"],
        pdfRuns: ["Personal expenses (rent, bills)"],
      },
      { runs: ["Established companies with significant revenue or funding"] },
      { runs: ["One-off events or other charities"] },
      { runs: ["Unclear deliverables"] },
    ],
  },
  { type: "heading", level: 1, text: "LP responsibilities", pdfBreak: true },
  { type: "p", runs: ["Being an LP is light-lift, high-impact. LPs:"] },
  {
    type: "list",
    items: [
      {
        runs: [{ b: "Join quarterly meetings." }, " Fun, social gatherings where we vote on winners."],
      },
      {
        runs: [{ b: "Review pitches." }, " Spend ~1 hour per month reading and rating applicants."],
      },
      {
        runs: [
          { b: "Pay $500/year." },
          " Directly funds our grants (may be increased slightly to cover chapter dues).",
        ],
      },
      {
        runs: [{ b: "Engage on Slack." }, " Stay active in our private channel and support founders."],
      },
      {
        runs: [
          { b: "Optional committees." },
          " Events, founder support, or social media, if desired.",
        ],
      },
    ],
    ordered: true,
  },
  {
    type: "p",
    runs: [
      "LPs are not investors. GNF is not a fund. There is no ROI. Only impact, community, and belief.",
    ],
  },
  { type: "heading", level: 1, text: "Setting up your chapter" },
  { type: "p", runs: ["We'll support you with:"] },
  {
    type: "list",
    items: [
      {
        runs: [
          "A chapter landing page on ",
          {
            a: { href: "https://goodneighbor.fund", text: "goodneighbor.fund", external: true },
          },
        ],
      },
      { runs: ["Access to our LP Portal (submission + review tool)"] },
      {
        runs: [
          "A ",
          { code: "@goodneighbor.fund", mono: true },
          " email address for the Chapter Director",
        ],
      },
      { runs: ["A private Slack community with a private chapter channel"] },
      {
        runs: ["Canva graphics kit + social media starter copy and assets (continuously updated)"],
        pdfRuns: ["Canva graphics kit + social media starter copy and assets"],
      },
      { runs: ["Knowledge base with event ideas, LP onboarding, and templates"] },
    ],
  },
  {
    type: "heading",
    level: 2,
    text: "Optional support services (additional cost per item)",
    pdfText: "Optional support services",
  },
  {
    type: "list",
    items: [
      { runs: ["Additional email address provisions"] },
      {
        runs: [
          "Banking services. Online banking to manage LP funds and distribute grant checks directly to awardees via mail, plus Stripe integration for recurring LP memberships",
        ],
        pdfRuns: ["Banking services. Online banking plus Stripe for recurring memberships"],
      },
      {
        runs: [
          "Custom ",
          {
            a: {
              href: "https://firebasestorage.googleapis.com/v0/b/gnf-app-9d7e3.firebasestorage.app/o/Misc%20Images%2FSansi-Oxford%20Pennant.webp?alt=media&token=35cfff2d-869d-45c9-b9f6-1b6b7550c5ea",
              text: "GNF Oxford Pennant",
              external: true,
            },
          },
          " ordering",
        ],
      },
      {
        runs: [
          "Custom ",
          {
            a: {
              href: "https://firebasestorage.googleapis.com/v0/b/gnf-app-9d7e3.firebasestorage.app/o/Misc%20Images%2Ffat-daddys.webp?alt=media&token=5474728d-52e3-4775-aed3-a828b1470a74",
              text: "GNF novelty check",
              external: true,
            },
          },
          " ordering",
        ],
      },
    ],
  },
  {
    type: "callout",
    label: "Chapter Dues",
    runs: [
      { b: "$25/month or $275/year." },
      " Covers hosting, maintenance, email, platform tools, and more.",
    ],
    pdfRuns: [
      "$25/month or $275/year. Covers hosting, maintenance, email, platform tools, and more.",
    ],
  },
  { type: "heading", level: 1, text: "Running a meeting", pdfBreak: true },
  {
    type: "list",
    items: [
      { runs: [{ b: "Pre-meeting." }, " LPs review and rate pitches asynchronously."] },
      {
        runs: [
          { b: "During meeting." },
          " Director facilitates; LPs share top picks, discuss, and vote.",
        ],
      },
      {
        runs: [
          { b: "Post-meeting." },
          " Director announces winner, coordinates disbursement, and shares publicly.",
        ],
      },
    ],
  },
  {
    type: "callout",
    label: "In person when possible",
    runs: [
      "Host in person when possible. Zoom is a suitable fallback if you're unable to align schedules.",
    ],
  },
  { type: "heading", level: 2, text: "How pitches are ranked" },
  {
    type: "p",
    runs: [
      "Each LP gives a pitch one of four ratings. Behind the scenes, every rating carries a weight:",
    ],
  },
  {
    type: "list",
    items: [
      { runs: [{ b: "Favorite" }, " = +2"] },
      { runs: [{ b: "Consideration" }, " = +1"] },
      { runs: [{ b: "Pass" }, " = 0"] },
      { runs: [{ b: "Ineligible" }, " = −2"] },
    ],
  },
  {
    type: "p",
    runs: [
      "When you sort pitches in the LP Portal, two numbers do the work.",
      { b: " Total Score" },
      " is the raw sum of those weights; it rewards volume of favorable reviews. ",
      { b: "Weighted Avg" },
      " (the default sort, shown as ",
      { i: "avg" },
      " on each card) is the smarter ranking, and it's the one we recommend leaning on.",
    ],
    pdfRuns: [
      "When you sort pitches in the LP Portal, two numbers do the work. Total Score is the raw sum of those weights; it rewards volume of favorable reviews. Weighted Avg (the default sort, shown as \"avg\" on each card) is the smarter ranking, and it's the one we recommend leaning on.",
    ],
  },
  {
    type: "p",
    runs: [
      "The Weighted Avg blends a pitch's actual rating average with the chapter-wide average, weighted by how many reviews it has. Pitches with lots of reviews stick close to their real average. Pitches with only one or two reviews get pulled toward the middle of the pack until more LPs weigh in. This stops a single five-star rave from outranking a pitch that's earned five Favorites and a Consideration, which would otherwise look worse on paper.",
    ],
  },
  {
    type: "callout",
    label: "What you'll see",
    runs: [
      "On the live review screen, each pitch shows three numbers: ",
      { b: "Score" },
      " (the raw sum), ",
      { b: "Avg" },
      " (the Weighted Avg used for sorting), and ",
      { b: "Raw" },
      " (the unweighted average). Hover any of them for a quick refresher.",
    ],
    pdfRuns: [
      "On the live review screen, each pitch shows three numbers: Score (the raw sum), Avg (the Weighted Avg used for sorting), and Raw (the unweighted average). Hover any of them for a quick refresher.",
    ],
  },
  {
    type: "p",
    runs: [
      "Scores are a starting point for discussion, not a verdict. The meeting is still where the decision happens; the rankings just help you spend that hour on the pitches most worth talking about.",
    ],
  },
  { type: "heading", level: 1, text: "Events & promotion" },
  { type: "p", runs: ["Chapters are encouraged to:"] },
  {
    type: "list",
    items: [
      { runs: ["Host local events (pitch nights, happy hours, hackathons, founder showcases)"] },
      {
        runs: ["Launch chapter-specific socials (e.g., ", { code: "@gnf.austin", mono: true }, ")"],
      },
      { runs: ["Partner with incubators, schools, and civic groups"] },
    ],
  },
  { type: "p", runs: ["We'll provide:"] },
  {
    type: "list",
    items: [
      { runs: ["Logos, Canva templates, and language guidelines"] },
      { runs: ["Sample posts and outreach copy"] },
      { runs: ["A media kit for local press"] },
    ],
  },
  { type: "heading", level: 1, text: "LP criteria: for directors recruiting LPs" },
  { type: "p", runs: ["We suggest inviting people who are:"] },
  {
    type: "list",
    items: [
      { runs: ["Past or present entrepreneurs"] },
      { runs: ["Actively supporting the local business community"] },
      { runs: ["Passionate about economic development or innovation"] },
      { runs: ["Collaborative, humble, and engaged"] },
      { runs: ["Open to mentoring or supporting grantees beyond the money"] },
    ],
  },
  {
    type: "callout",
    label: "Avoid",
    runs: ["Self-promotion, ego plays, or disengaged funders."],
  },
  { type: "heading", level: 1, text: "FAQs", pdfBreak: true },
  { type: "heading", level: 2, text: "What if our LPs can't attend a meeting?" },
  {
    type: "p",
    runs: ["We recommend 2 out of 4 per year minimum. Remote voting is okay in a pinch."],
  },
  { type: "heading", level: 2, text: "Can I invest in GNF grantees personally?" },
  {
    type: "p",
    runs: [
      "Yes. GNF is not exclusive. Feel free to follow up outside of our platform if you want to support further.",
    ],
  },
  { type: "heading", level: 2, text: "How do we handle the money?" },
  {
    type: "p",
    runs: [
      "Up to you. Some chapters use a shared bank account. Others just Venmo. Or you can leverage our services. Choose what works.",
    ],
  },
  { type: "heading", level: 2, text: "Can I bring a guest to a meeting?" },
  {
    type: "p",
    runs: ["Absolutely. Some chapters allow guest LPs to vote, others just observe. Your call."],
  },
  { type: "heading", level: 1, text: "Ready to launch?" },
  {
    type: "p",
    runs: ["We've got everything you need. Just submit your New Chapter Form. If accepted, we will:"],
  },
  {
    type: "list",
    items: [
      { runs: ["Schedule an intro call"] },
      { runs: ["Get you a starter kit + backend access"] },
      { runs: ["Invite you to the national Slack channel"] },
    ],
  },
  { type: "p", runs: ["Let's bring belief capital to your block."] },
  { type: "heading", level: 1, text: "From the founders", pdfBreak: true },
  { type: "p", runs: ["Hey there. We're Susan and Jason, co-founders of Good Neighbor Fund."] },
  {
    type: "p",
    runs: [
      "We launched GNF with a borrowed logo and $1,000 in belief. What we saw immediately was the hunger for this kind of support. For simple, accessible, joyful ways to help people start.",
    ],
  },
  {
    type: "p",
    runs: [
      "Whether you start a chapter or just share this idea with a friend, you're a part of something that's growing bigger every day. Thanks for believing in others before the rest of the world catches on.",
    ],
  },
  {
    type: "signature",
    lines: ["With love,", "Jason and Susan"],
    caption: "Co-Founders, Good Neighbor Fund",
  },
];
